import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router';
import { ProtectedRoute } from './ProtectedRoute';

/* ---------- Shared components ---------- */

function FullPageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-[#07070B]">
      <div className="h-8 w-8 rounded-full bg-[#9B9BA8] animate-pulse" />
    </div>
  );
}

/* ---------- Phase 3: Auth pages (real implementations) ---------- */

const LoginPage = lazy(() =>
  import('../pages/auth/LoginPage').then((m) => ({ default: m.LoginPage }))
);
const RegisterPage = lazy(() =>
  import('../pages/auth/RegisterPage').then((m) => ({ default: m.RegisterPage }))
);
const VerifyPage = lazy(() =>
  import('../pages/auth/VerifyPage').then((m) => ({ default: m.VerifyPage }))
);
const AcceptInvitePage = lazy(() =>
  import('../pages/auth/AcceptInvitePage').then((m) => ({ default: m.AcceptInvitePage }))
);
const SetupPage = lazy(() =>
  import('../pages/auth/SetupWizardPage').then((m) => ({ default: m.SetupWizardPage }))
);
const ForgotPasswordPage = lazy(() =>
  import('../pages/auth/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage }))
);

const CheckoutSuccessPage = lazy(() =>
  import('../pages/auth/CheckoutSuccessPage').then((m) => ({ default: m.CheckoutSuccessPage }))
);

/* ---------- Lazy-loaded portal routes ---------- */

const ClientRoutes = lazy(() =>
  import('./ClientRoutes').then((m) => ({ default: m.ClientRoutes }))
);

/* ---------- Lazy-loaded operator shell (real component) ---------- */

const OperatorShell = lazy(() =>
  import('../components/layout/OperatorShell').then((m) => ({ default: m.OperatorShell }))
);

/* ---------- Phase 1: Command Center pages ---------- */

const CommandCenterPage = lazy(() =>
  import('../pages/command-center/CommandCenterPage').then((m) => ({ default: m.CommandCenterPage }))
);
const AlertsPage = lazy(() =>
  import('../pages/command-center/AlertsPage').then((m) => ({ default: m.AlertsPage }))
);
const ActionQueuePage = lazy(() =>
  import('../pages/command-center/ActionQueuePage').then((m) => ({ default: m.ActionQueuePage }))
);
const AtlasConsolePage = lazy(() =>
  import('../pages/command-center/AtlasConsolePage').then((m) => ({ default: m.AtlasConsolePage }))
);
const BriefingsPage = lazy(() =>
  import('../pages/command-center/BriefingsPage').then((m) => ({ default: m.BriefingsPage }))
);
const ExecutionLogPage = lazy(() =>
  import('../pages/command-center/ExecutionLogPage').then((m) => ({ default: m.ExecutionLogPage }))
);

/* ---------- Phase 2: Business Memory CRM ---------- */

const ContactsPage = lazy(() =>
  import('../pages/crm/ContactsPage').then((m) => ({ default: m.ContactsPage }))
);
const ContactDetailPage = lazy(() =>
  import('../pages/crm/ContactDetailPage').then((m) => ({ default: m.ContactDetailPage }))
);
const CompaniesPage = lazy(() =>
  import('../pages/crm/CompaniesPage').then((m) => ({ default: m.CompaniesPage }))
);
const CompanyDetailPage = lazy(() =>
  import('../pages/crm/CompanyDetailPage').then((m) => ({ default: m.CompanyDetailPage }))
);
const PipelinePage = lazy(() =>
  import('../pages/crm/PipelinePage').then((m) => ({ default: m.PipelinePage }))
);
const ConversationsPage = lazy(() =>
  import('../pages/crm/ConversationsPage').then((m) => ({ default: m.ConversationsPage }))
);

/* ---------- Phase 4: Intelligence ---------- */

const IntelligencePage = lazy(() =>
  import('../pages/intelligence/IntelligencePage').then((m) => ({ default: m.IntelligencePage }))
);
const CognitionPage = lazy(() =>
  import('../pages/intelligence/CognitionPage').then((m) => ({ default: m.CognitionPage }))
);
const CognitionDetailPage = lazy(() =>
  import('../pages/intelligence/CognitionDetailPage').then((m) => ({ default: m.CognitionDetailPage }))
);

/* ---------- Phase 5: Command Additions ---------- */

const InboxPage = lazy(() =>
  import('../pages/command-center/InboxPage').then((m) => ({ default: m.InboxPage }))
);
const NotificationsPage = lazy(() =>
  import('../pages/command-center/NotificationsPage').then((m) => ({ default: m.NotificationsPage }))
);

/* ---------- Phase 5: Customer Hub ---------- */

const CustomersPage = lazy(() =>
  import('../pages/customers/CustomersPage').then((m) => ({ default: m.CustomersPage }))
);
const IdentityPage = lazy(() =>
  import('../pages/customers/IdentityPage').then((m) => ({ default: m.IdentityPage }))
);

/* ---------- Phase 5: Execution ---------- */

const ExecutionPage = lazy(() =>
  import('../pages/execution/ExecutionPage').then((m) => ({ default: m.ExecutionPage }))
);

/* ---------- Phase 6: Governance ---------- */

const GovernancePage = lazy(() =>
  import('../pages/governance/GovernancePage').then((m) => ({ default: m.GovernancePage }))
);

/* ---------- Phase 7: Settings & Team ---------- */

const SettingsPage = lazy(() =>
  import('../pages/settings/SettingsPage').then((m) => ({ default: m.SettingsPage }))
);

/* ---------- Phase 8: Admin ---------- */

const AdminPage = lazy(() =>
  import('../pages/admin/AdminPage').then((m) => ({ default: m.AdminPage }))
);

/* ---------- Phase 8: Verticals ---------- */

const HealthcarePage = lazy(() =>
  import('../pages/verticals/HealthcarePage').then((m) => ({ default: m.HealthcarePage }))
);
const HomeServicesPage = lazy(() =>
  import('../pages/verticals/HomeServicesPage').then((m) => ({ default: m.HomeServicesPage }))
);

/* ---------- Phase 8: Execution Depth ---------- */

const CallDetailPage = lazy(() =>
  import('../pages/execution/CallDetailPage').then((m) => ({ default: m.CallDetailPage }))
);
const AgentDetailPage = lazy(() =>
  import('../pages/execution/AgentDetailPage').then((m) => ({ default: m.AgentDetailPage }))
);
const DocumentsPage = lazy(() =>
  import('../pages/documents/DocumentsPage').then((m) => ({ default: m.DocumentsPage }))
);

/* ---------- Phase 8: Cross-cutting ---------- */

const NotFoundPage = lazy(() =>
  import('../pages/NotFoundPage').then((m) => ({ default: m.NotFoundPage }))
);

/* ---------- App Routes ---------- */

export function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          <Suspense fallback={<FullPageLoader />}>
            <LoginPage />
          </Suspense>
        }
      />
      <Route
        path="/register"
        element={
          <Suspense fallback={<FullPageLoader />}>
            <RegisterPage />
          </Suspense>
        }
      />
      <Route
        path="/verify"
        element={
          <Suspense fallback={<FullPageLoader />}>
            <VerifyPage />
          </Suspense>
        }
      />
      <Route
        path="/accept-invite"
        element={
          <Suspense fallback={<FullPageLoader />}>
            <AcceptInvitePage />
          </Suspense>
        }
      />
      <Route
        path="/setup"
        element={
          <Suspense fallback={<FullPageLoader />}>
            <SetupPage />
          </Suspense>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <Suspense fallback={<FullPageLoader />}>
            <ForgotPasswordPage />
          </Suspense>
        }
      />
      <Route
        path="/checkout/success"
        element={
          <Suspense fallback={<FullPageLoader />}>
            <CheckoutSuccessPage />
          </Suspense>
        }
      />

      {/* Portal routes */}
      <Route
        path="/portal/*"
        element={
          <Suspense fallback={<FullPageLoader />}>
            <ClientRoutes />
          </Suspense>
        }
      />

      {/* All operator routes — protected, inside OperatorShell layout */}
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <Suspense fallback={<FullPageLoader />}>
              <OperatorShell />
            </Suspense>
          </ProtectedRoute>
        }
      >
        {/* Phase 1: Command Center */}
        <Route index element={<CommandCenterPage />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="alerts/:id" element={<AlertsPage />} />
        <Route path="action-queue" element={<ActionQueuePage />} />
        <Route path="action-queue/:id" element={<ActionQueuePage />} />
        <Route path="atlas" element={<AtlasConsolePage />} />
        <Route path="atlas/:sessionId" element={<AtlasConsolePage />} />
        <Route path="briefings" element={<BriefingsPage />} />
        <Route path="briefings/:id" element={<BriefingsPage />} />
        <Route path="execution-log" element={<ExecutionLogPage />} />

        {/* Phase 5: Command additions */}
        <Route path="dashboard" element={<CommandCenterPage />} />
        <Route path="inbox" element={<InboxPage />} />
        <Route path="notifications" element={<NotificationsPage />} />

        {/* Phase 2: Business Memory CRM */}
        <Route path="crm/contacts" element={<ContactsPage />} />
        <Route path="crm/contacts/:id" element={<ContactDetailPage />} />
        <Route path="crm/companies" element={<CompaniesPage />} />
        <Route path="crm/companies/:id" element={<CompanyDetailPage />} />
        <Route path="crm/pipeline" element={<PipelinePage />} />
        <Route path="conversations" element={<ConversationsPage />} />

        {/* Phase 5: Customer Hub */}
        <Route path="customers" element={<CustomersPage />} />
        <Route path="identity" element={<IdentityPage />} />

        {/* Phase 4: Intelligence */}
        <Route path="intelligence" element={<IntelligencePage />} />
        <Route path="intelligence/dossiers" element={<IntelligencePage />} />
        <Route path="intelligence/segments" element={<IntelligencePage />} />
        <Route path="intelligence/workflows" element={<IntelligencePage />} />
        <Route path="intelligence/twins" element={<IntelligencePage />} />
        <Route path="intelligence/predictions" element={<IntelligencePage />} />
        <Route path="intelligence/playbooks" element={<IntelligencePage />} />
        <Route path="intelligence/analytics" element={<IntelligencePage />} />
        <Route path="intelligence/cognition" element={<CognitionPage />} />
        <Route path="intelligence/cognition/:id" element={<CognitionDetailPage />} />

        {/* Phase 5: Execution */}
        <Route path="execution" element={<ExecutionPage />} />
        <Route path="execution/agents" element={<ExecutionPage />} />
        <Route path="execution/workflows" element={<ExecutionPage />} />
        <Route path="execution/comms" element={<ExecutionPage />} />
        <Route path="execution/meetings" element={<ExecutionPage />} />
        <Route path="execution/campaigns" element={<ExecutionPage />} />
        <Route path="execution/escalations" element={<ExecutionPage />} />
        <Route path="execution/comms/calls/:callId" element={<CallDetailPage />} />
        <Route path="execution/agents/:name" element={<AgentDetailPage />} />
        <Route path="documents" element={<DocumentsPage />} />

        {/* Legacy redirects */}
        <Route path="agents" element={<Navigate to="/execution/agents" replace />} />
        <Route path="campaigns" element={<Navigate to="/execution/workflows" replace />} />
        <Route path="comms" element={<Navigate to="/execution/comms" replace />} />
        <Route path="meetings" element={<Navigate to="/execution/meetings" replace />} />

        {/* Phase 6: Governance */}
        <Route path="governance" element={<GovernancePage />} />
        <Route path="governance/decisions" element={<GovernancePage />} />
        <Route path="governance/audit-log" element={<GovernancePage />} />
        <Route path="governance/policies" element={<GovernancePage />} />
        <Route path="governance/compliance" element={<GovernancePage />} />

        {/* Phase 6: Intelligence additions */}
        <Route path="intelligence/advisor" element={<IntelligencePage />} />
        <Route path="intelligence/revenue" element={<IntelligencePage />} />
        <Route path="intelligence/observatory" element={<IntelligencePage />} />
        <Route path="intelligence/ab-tests" element={<IntelligencePage />} />
        <Route path="intelligence/reports" element={<IntelligencePage />} />

        {/* Legacy governance redirects */}
        <Route path="governance/autonomy" element={<Navigate to="/governance" replace />} />
        <Route path="governance/consent" element={<Navigate to="/governance/policies" replace />} />
        <Route path="governance/budgets" element={<Navigate to="/governance/compliance" replace />} />

        {/* Legacy proof redirects */}
        <Route path="proof/trace" element={<Navigate to="/governance/decisions" replace />} />
        <Route path="proof/audit" element={<Navigate to="/governance/audit-log" replace />} />
        <Route path="proof/lift" element={<Navigate to="/governance/compliance" replace />} />
        <Route path="proof/ab-tests" element={<Navigate to="/intelligence/ab-tests" replace />} />
        <Route path="proof/revenue" element={<Navigate to="/intelligence/revenue" replace />} />
        <Route path="proof/reports" element={<Navigate to="/intelligence?tab=strategy" replace />} />

        {/* Phase 7: Settings & Team */}
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/communication" element={<SettingsPage />} />
        <Route path="settings/billing" element={<SettingsPage />} />
        <Route path="settings/help" element={<SettingsPage />} />
        <Route path="settings/security" element={<SettingsPage />} />
        <Route path="settings/team/members" element={<SettingsPage />} />
        <Route path="settings/team/roles" element={<SettingsPage />} />
        <Route path="settings/team/invitations" element={<SettingsPage />} />
        <Route path="settings/team/provisioning" element={<SettingsPage />} />
        <Route path="settings/team/audit" element={<SettingsPage />} />

        {/* Settings — category routes */}
        <Route path="settings/ai" element={<SettingsPage />} />
        <Route path="settings/compliance" element={<SettingsPage />} />
        {/* Redirect orphan settings routes to their closest match */}
        <Route path="settings/profile" element={<Navigate to="/settings" replace />} />
        <Route path="settings/integrations" element={<Navigate to="/settings/help" replace />} />
        <Route path="settings/team/workspaces" element={<Navigate to="/settings/team/members" replace />} />

        {/* Settings redirects */}
        <Route path="settings/calendar" element={<Navigate to="/settings" replace />} />
        <Route path="settings/team" element={<Navigate to="/settings/team/members" replace />} />

        {/* Admin */}
        <Route path="admin" element={<AdminPage />} />
        <Route path="admin/tenants" element={<AdminPage />} />
        <Route path="admin/health" element={<AdminPage />} />
        <Route path="admin/security" element={<AdminPage />} />
        <Route path="admin/features" element={<AdminPage />} />
        <Route path="admin/llm" element={<AdminPage />} />
        <Route path="admin/budgets" element={<AdminPage />} />
        <Route path="admin/agents" element={<AdminPage />} />
        <Route path="admin/bundles" element={<AdminPage />} />
        <Route path="admin/knowledge" element={<AdminPage />} />
        <Route path="admin/integrations" element={<AdminPage />} />
        <Route path="admin/webhooks" element={<AdminPage />} />
        <Route path="admin/crm-import" element={<AdminPage />} />
        <Route path="admin/data" element={<AdminPage />} />
        <Route path="admin/usage" element={<AdminPage />} />
        <Route path="admin/ci" element={<AdminPage />} />
        <Route path="admin/support" element={<AdminPage />} />
        <Route path="admin/reputation" element={<AdminPage />} />
        <Route path="admin/portal" element={<AdminPage />} />
        <Route path="admin/branding" element={<AdminPage />} />
        <Route path="vertical-config" element={<AdminPage />} />

        {/* Phase 8: Verticals */}
        <Route path="healthcare/dashboard" element={<HealthcarePage />} />
        <Route path="healthcare/clinical" element={<HealthcarePage />} />
        <Route path="healthcare/coding" element={<HealthcarePage />} />
        <Route path="healthcare/fhir" element={<HealthcarePage />} />
        <Route path="healthcare/fhir/manage" element={<HealthcarePage />} />
        <Route path="healthcare/patient-finance" element={<HealthcarePage />} />
        <Route path="healthcare/claims" element={<HealthcarePage />} />
        <Route path="healthcare/denials" element={<HealthcarePage />} />
        <Route path="healthcare/rcm" element={<HealthcarePage />} />
        <Route path="healthcare/preauth" element={<HealthcarePage />} />
        <Route path="healthcare/compliance" element={<HealthcarePage />} />
        <Route path="healthcare/coding" element={<HealthcarePage />} />
        <Route path="home-services/dashboard" element={<HomeServicesPage />} />
        <Route path="home-services/routes" element={<HomeServicesPage />} />
        <Route path="home-services/analytics" element={<HomeServicesPage />} />

        {/* Catch-all */}
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
